import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismCard, PrismTooltip } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { and, not } from 'ember-truth-helpers';
import {
  BENI_COMPARISON_SETS,
  type ComparisonSet,
} from 'frontend-utils/utils/constants/benchmark-insight';
import SkeletonPaceChartIllustration from 'lumen/assets/images/benchmark-insight/skeleton/skeleton-pace-chart.svg?raw';
import HotelBenchmarkInsightChartsPaceChart from 'lumen/components/hotel/benchmark-insight/charts/pace-chart';
import HotelBenchmarkInsightSectionsSectionPaceSectionPaceOverlay from 'lumen/components/hotel/benchmark-insight/sections/section-pace/section-pace-overlay';
import HotelBenchmarkInsightSkeleton from 'lumen/components/hotel/benchmark-insight/skeleton';
import HotelBenchmarkInsightUpsellIcon from 'lumen/components/hotel/benchmark-insight/upsell-icon';
import type {
  BeniBreakdown,
  BeniMeta,
  BeniMetaComparisonSet,
} from 'lumen/models';
import {
  KPIS,
  type ReferencePeriod,
} from 'lumen/utils/constants/benchmark-insight/beni-constant-values';
import { DATE_AGGREGATION } from 'lumen/utils/constants/general-constants';

import styles from './section-pace.module.css';

interface HotelBenchmarkInsightSectionsSectionPaceSectionPaceSignature {
  Args: {
    breakdownsByComparisonSet: Record<ComparisonSet, BeniBreakdown[]>;
    meta: BeniMeta;
    primaryComparisonSetType: ComparisonSet;
    referencePeriod: ReferencePeriod;
    secondaryComparisonSetType: ComparisonSet;
    showSkeleton: boolean;
    showSkeletonAnimation: boolean;
  };
}

export default class HotelBenchmarkInsightSectionsSectionPaceSectionPace extends Component<HotelBenchmarkInsightSectionsSectionPaceSectionPaceSignature> {
  @service('benchmark-insight/features')
  declare benchmarkInsightFeatures: Services['benchmark-insight/features'];

  dateAggregation = DATE_AGGREGATION.DAY;
  selectedKpi = KPIS.OCCUPANCY;

  get ownComparisonSetBreakdowns(): BeniBreakdown[] {
    const { breakdownsByComparisonSet } = this.args;

    return this.getComparisonSetBreakdowns(
      breakdownsByComparisonSet,
      BENI_COMPARISON_SETS.OWN,
    );
  }

  get ownComparisonSetMeta(): BeniMetaComparisonSet | null {
    const { meta } = this.args;

    if (!meta) {
      return null;
    }

    return meta.getMetaForComparisonSet(BENI_COMPARISON_SETS.OWN);
  }

  get primaryComparisonSetBreakdowns(): BeniBreakdown[] {
    const { breakdownsByComparisonSet, primaryComparisonSetType } = this.args;

    return this.getComparisonSetBreakdowns(
      breakdownsByComparisonSet,
      primaryComparisonSetType,
    );
  }

  get primaryComparisonSetMeta(): BeniMetaComparisonSet | null {
    const { meta, primaryComparisonSetType } = this.args;

    if (!meta || !primaryComparisonSetType) {
      return null;
    }

    return meta.getMetaForComparisonSet(primaryComparisonSetType);
  }

  get secondaryComparisonSetBreakdowns(): BeniBreakdown[] {
    const { breakdownsByComparisonSet, secondaryComparisonSetType } = this.args;

    return this.getComparisonSetBreakdowns(
      breakdownsByComparisonSet,
      secondaryComparisonSetType,
    );
  }

  get secondaryComparisonSetMeta(): BeniMetaComparisonSet | null {
    const { meta, secondaryComparisonSetType } = this.args;

    if (!meta || !secondaryComparisonSetType) {
      return null;
    }

    return meta.getMetaForComparisonSet(secondaryComparisonSetType);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showSkeleton() {
    return (
      this.args.showSkeleton || this.benchmarkInsightFeatures.showTrialCtas
    );
  }

  getComparisonSetBreakdowns(
    breakdownsByComparisonSet: Partial<Record<ComparisonSet, BeniBreakdown[]>>,
    comparisonSet: ComparisonSet,
  ): BeniBreakdown[] {
    if (!breakdownsByComparisonSet) {
      return [];
    }

    const comparisonSetBreakdowns = breakdownsByComparisonSet[comparisonSet];
    if (!comparisonSetBreakdowns) {
      return [];
    }

    return comparisonSetBreakdowns;
  }

  <template>
    <PrismCard
      @contentLayout="noPadding"
      class="{{styles.container}} ic-product-tour-trial-onboarding-pace"
    >
      <:header>
        <div class="flex items-center">
          {{#if this.benchmarkInsightFeatures.showTrialTeasers}}
            <div class="mr-200">
              <HotelBenchmarkInsightUpsellIcon />

              <PrismTooltip
                @text={{this.benchmarkInsightFeatures.beniLightTooltipText}}
              />
            </div>
          {{/if}}

          <p class="text-400--bold">
            {{t "general.pace"}}
          </p>
        </div>
      </:header>

      <:content>
        <div class={{styles.card-content}}>
          {{#if this.showSkeleton}}
            <div class={{styles.skeleton}}>
              <HotelBenchmarkInsightSkeleton
                @imageHtml={{SkeletonPaceChartIllustration}}
                @showAnimation={{and
                  @showSkeletonAnimation
                  (not this.benchmarkInsightFeatures.showTrialCtas)
                }}
              />
            </div>
          {{else}}
            <HotelBenchmarkInsightChartsPaceChart
              @dateAggregation={{this.dateAggregation}}
              @extractDate={{@meta.extractDate}}
              @ownComparisonSetBreakdowns={{this.ownComparisonSetBreakdowns}}
              @ownComparisonSetMeta={{this.ownComparisonSetMeta}}
              @primaryComparisonSetBreakdowns={{this.primaryComparisonSetBreakdowns}}
              @primaryComparisonSetMeta={{this.primaryComparisonSetMeta}}
              @referencePeriod={{@referencePeriod}}
              @secondaryComparisonSetBreakdowns={{this.secondaryComparisonSetBreakdowns}}
              @secondaryComparisonSetMeta={{this.secondaryComparisonSetMeta}}
              @selectedKpi={{this.selectedKpi}}
            />
          {{/if}}

          {{#if this.benchmarkInsightFeatures.showTrialCtas}}
            <HotelBenchmarkInsightSectionsSectionPaceSectionPaceOverlay />
          {{/if}}
        </div>
      </:content>
    </PrismCard>
  </template>
}
